import axios from "axios";
import {
  attachRefreshTokenInterceptor,
  attachTransientRetryInterceptor,
  clearAuthToken,
  applyEmbeddedHeaders,
  applyExploreTrialHeader,
  getAuthToken,
  getAuthIdToken,
  setAuthIdToken,
  setAuthRefreshToken,
  setAuthToken,
} from "./client";

const AUTH_API_BASE_URL =
  process.env.REACT_APP_AUTH_API_URL ||
  process.env.REACT_APP_API_URL //||
   //"https://1maj7uejdd.execute-api.ap-south-1.amazonaws.com/prod_korv_rfm_api_stage";

const authClient = axios.create({
  baseURL: AUTH_API_BASE_URL,
  timeout: 60000,
});

// Add token to all requests
authClient.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return applyEmbeddedHeaders(applyExploreTrialHeader(config));
});

attachRefreshTokenInterceptor(authClient);
attachTransientRetryInterceptor(authClient);
authClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const currentMessage = String(error?.message || "").trim();
    if (error?.code === "ECONNABORTED" || /timeout/i.test(currentMessage)) {
      error.message =
        "The request timed out. This can happen if the network is slow, unavailable, or the server took too long to respond.";
    } else if (!error?.response) {
      error.message =
        "The request could not reach the server. Please check your network connection or server availability.";
    }
    return Promise.reject(error);
  }
);

export async function loginWithPassword(email, password) {
  const normalizedEmail = (email || "").trim().toLowerCase();
  const normalizedPassword = (password || "").trim();

  if (!normalizedEmail || !normalizedPassword) {
    const error = new Error("Email and password are required");
    error.response = { data: { detail: "Email and password are required." } };
    throw error;
  }

  const { data } = await authClient.post("/auth/login", {
    email: normalizedEmail,
    password: normalizedPassword,
  });

  const token = data?.access_token || "";
  const idToken = data?.id_token || "";
  const refreshToken = data?.refresh_token || "";
  if (!token) {
    const error = new Error("Login did not return access token");
    error.response = { data: { detail: "Login failed: token not returned." } };
    throw error;
  }

  setAuthToken(token);
  setAuthIdToken(idToken);
  setAuthRefreshToken(refreshToken);

  try {
    const me = await getCurrentUserProfile();
    return {
      token,
      mode: "password",
      profile: me,
      passwordChangeRequired: Boolean(data?.password_change_required),
    };
  } catch (error) {
    clearAuthToken();
    throw error;
  }
}

export async function changeCurrentUserPassword(currentPassword, newPassword, confirmNewPassword) {
  const payload = {
    current_password: (currentPassword || "").trim(),
    new_password: (newPassword || "").trim(),
    confirm_new_password: (confirmNewPassword || "").trim(),
  };

  const { data } = await authClient.post("/auth/change-password", payload);
  return data;
}

export async function getOAuthLoginUrl(redirectUri, options = {}) {
  const normalizedRedirectUri = (redirectUri || "").trim();
  if (!normalizedRedirectUri) {
    const error = new Error("redirectUri is required");
    error.response = { data: { detail: "redirectUri is required." } };
    throw error;
  }

  const forceAccountSelection = Boolean(options.forceAccountSelection);
  const forceReauthentication = Boolean(options.forceReauthentication);
  const loginHint = (options.loginHint || "").trim().toLowerCase();
  const codeChallenge = (options.codeChallenge || "").trim();
  const codeChallengeMethod = (options.codeChallengeMethod || "S256").trim();

  if (!codeChallenge) {
    const error = new Error("codeChallenge is required");
    error.response = { data: { detail: "codeChallenge is required." } };
    throw error;
  }

  const { data } = await authClient.get("/auth/oauth/url", {
    params: {
      redirect_uri: normalizedRedirectUri,
      code_challenge: codeChallenge,
      code_challenge_method: codeChallengeMethod,
      force_account_selection: forceAccountSelection,
      force_reauthentication: forceReauthentication,
      login_hint: loginHint,
    },
    timeout: 15000,
  });
  return data;
}

export async function getOAuthLogoutUrl(redirectUri, options = {}) {
  const normalizedRedirectUri = (redirectUri || "").trim();
  if (!normalizedRedirectUri) {
    const error = new Error("redirectUri is required");
    error.response = { data: { detail: "redirectUri is required." } };
    throw error;
  }

  const idTokenHint = (options.idTokenHint || getAuthIdToken() || "").trim();
  const forceAccountSelection = Boolean(options.forceAccountSelection);
  const forceReauthentication = Boolean(options.forceReauthentication);
  const codeChallenge = (options.codeChallenge || "").trim();
  const codeChallengeMethod = (options.codeChallengeMethod || "S256").trim();

  const { data } = await authClient.get("/auth/logout/url", {
    params: {
      redirect_uri: normalizedRedirectUri,
      id_token_hint: idTokenHint,
      force_account_selection: forceAccountSelection,
      force_reauthentication: forceReauthentication,
      code_challenge: codeChallenge,
      code_challenge_method: codeChallengeMethod,
    },
    timeout: 15000,
  });
  return data;
}

export async function getAuthMethods() {
  const { data } = await authClient.get("/auth/methods", {
    timeout: 15000,
  });
  return data;
}

export async function exchangeOAuthCode(code, redirectUri, codeVerifier, exploreTrialId) {
  const normalizedCode = (code || "").trim();
  const normalizedRedirectUri = (redirectUri || "").trim();
  const normalizedCodeVerifier = (codeVerifier || "").trim();
  if (!normalizedCode || !normalizedRedirectUri || !normalizedCodeVerifier) {
    const error = new Error("code, redirectUri, and codeVerifier are required");
    error.response = { data: { detail: "code, redirectUri, and codeVerifier are required." } };
    throw error;
  }

  const payload = {
    code: normalizedCode,
    redirect_uri: normalizedRedirectUri,
    code_verifier: normalizedCodeVerifier,
  };
  if (exploreTrialId) {
    payload.explore_trial_id = String(exploreTrialId);
  }

  const { data } = await authClient.post("/auth/oauth/exchange", payload);

  if (data?.is_authorized === false) {
    return {
      mode: "oauth",
      is_authorized: false,
      email: data?.email || "",
      logout_url: data?.logout_url || "",
      state: data?.state || "",
      reason: data?.reason || "User is not authorized",
    };
  }

  const token = data?.access_token || "";
  const idToken = data?.id_token || "";
  const refreshToken = data?.refresh_token || "";
  if (!token) {
    const error = new Error("OAuth exchange did not return access token");
    error.response = { data: { detail: "OAuth login failed: token not returned." } };
    throw error;
  }

  setAuthToken(token);
  setAuthIdToken(idToken);
  setAuthRefreshToken(refreshToken);
  try {
    const me = await getCurrentUserProfile();
    return { token, mode: "oauth", profile: me, is_authorized: true };
  } catch (error) {
    clearAuthToken();
    throw error;
  }
}

export async function getCurrentUserProfile() {
  const token = String(getAuthToken() || "");

  const { data } = await authClient.get("/auth/me", {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  return data;
}

export async function silentLogoutSession(refreshToken = "") {
  const normalizedRefreshToken = (refreshToken || "").trim();
  const { data } = await authClient.post("/auth/logout/silent", {
    refresh_token: normalizedRefreshToken || null,
  });
  return data;
}

export function logoutSession() {
  clearAuthToken();
  setAuthIdToken("");
  setAuthRefreshToken("");
}

export async function createExploreTrial(email, phone) {
  const normalizedEmail = (email || "").trim().toLowerCase();
  if (!normalizedEmail) {
    const error = new Error("email is required");
    error.response = { data: { detail: "email is required" } };
    throw error;
  }
  const params = {
    email: normalizedEmail,
  };
  if (phone) {
    params.phone = phone;  // Backend expects 'phone' parameter
  }
  const { data } = await authClient.post(
    "/auth/trial/start",
    null,
    {
      params,
    }
  );
  return data;
}

export async function saveTrialPhone(phone, fullName = "") {
  const normalizedPhone = (phone || "").trim();
  if (!normalizedPhone) {
    const error = new Error("phone is required");
    error.response = { data: { detail: "phone is required" } };
    throw error;
  }
  const normalizedFullName = (fullName || "").trim();
  const { data } = await authClient.post(
    "/auth/trial/phone",
    null,
    {
      params: {
        phone: normalizedPhone,
        full_name: normalizedFullName,
      },
    }
  );
  return data;
}

export async function listTrialAccounts() {
  const { data } = await authClient.get("/auth/trial/accounts", {
    timeout: 15000,
  });
  return data;
}

export async function blockTrialAccount(email) {
  const normalizedEmail = (email || "").trim().toLowerCase();
  const { data } = await authClient.post(
    `/auth/trial/accounts/${normalizedEmail}/block`,
    null
  );
  return data;
}

export async function restoreTrialAccount(email) {
  const normalizedEmail = (email || "").trim().toLowerCase();
  const { data } = await authClient.post(
    `/auth/trial/accounts/${normalizedEmail}/restore`,
    null
  );
  return data;
}

export async function deleteTrialAccount(email) {
  const normalizedEmail = (email || "").trim().toLowerCase();
  const { data } = await authClient.delete(`/auth/trial/accounts/${normalizedEmail}`);
  return data;
}
